import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, UserCheck, UserX, LogIn, LogOut, Copy } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errText = (e) => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : d?.message || 'Error'; };
const ROLE = { pilot: 'Piloto', cabin_crew: 'Tripulante de cabina' };

export function useCrewAccounts() {
    const [accounts, setAccounts] = useState({});
    const reload = useCallback(() => axios.get(`${API}/crew-accounts`).then(r => setAccounts(r.data)).catch(() => setAccounts({})), []);
    useEffect(() => { reload(); }, [reload]);
    return [accounts, reload];
}

function TempPasswordDialog({ result, onClose }) {
    return (
        <Dialog open={!!result} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="crew-temp-password-dialog">
                <DialogHeader>
                    <DialogTitle>Cuenta creada</DialogTitle>
                    <DialogDescription>Esta contraseña temporal se muestra una sola vez. Entrégala al tripulante: deberá cambiarla en su primer ingreso.</DialogDescription>
                </DialogHeader>
                <div className="space-y-2 text-sm">
                    <p>Correo: <span className="font-mono">{result?.email}</span> · Rol: {ROLE[result?.role]}</p>
                    <div className="flex gap-2">
                        <Input readOnly value={result?.temporary_password || ''} className="font-mono" data-testid="crew-temp-password" />
                        <Button variant="outline" size="icon" onClick={() => { navigator.clipboard?.writeText(result.temporary_password); toast.success('Copiada'); }}><Copy className="w-4 h-4" /></Button>
                    </div>
                </div>
                <DialogFooter><Button onClick={onClose} data-testid="crew-temp-password-close">Entendido</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function CrewAccountControls({ c, account, onChanged }) {
    const { hasRole } = useAuth();
    const [result, setResult] = useState(null);
    const manage = hasRole(['admin', 'crew_admin']);
    const onBehalf = hasRole(['admin', 'crew_admin', 'ccod']);
    if (!account || !account.role_for_position) return null;
    const run = async (fn, ok) => { try { const r = await fn(); if (ok) toast.success(ok); onChanged(); return r; } catch (e) { toast.error(errText(e)); return null; } };
    const create = async () => { const r = await run(() => axios.post(`${API}/crew/${c.id}/account`, {})); if (r) setResult(r.data); };
    return (
        <div className="flex flex-wrap items-center gap-1 mt-2 justify-end" data-testid={`crew-account-${c.id}`}>
            {account.has_account ? (
                <Badge variant="outline" className={account.active ? 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'} data-testid={`crew-account-status-${c.id}`}>
                    {account.active ? `Cuenta activa · ${ROLE[account.role]} · ${account.email}` : `Cuenta desactivada · ${account.email}`}{account.must_change_password ? ' · pendiente 1er ingreso' : ''}
                </Badge>
            ) : <Badge variant="outline" className="text-muted-foreground" data-testid={`crew-account-status-${c.id}`}>Sin cuenta</Badge>}
            {manage && !account.has_account && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={create} data-testid={`crew-account-create-${c.id}`}><KeyRound className="w-3 h-3 mr-1" />Crear cuenta</Button>}
            {manage && account.has_account && (account.active
                ? <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => run(() => axios.patch(`${API}/crew/${c.id}/account`, { active: false }), 'Cuenta desactivada')} data-testid={`crew-account-deactivate-${c.id}`}><UserX className="w-3 h-3 mr-1" />Desactivar cuenta</Button>
                : <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => run(() => axios.patch(`${API}/crew/${c.id}/account`, { active: true }), 'Cuenta reactivada')} data-testid={`crew-account-activate-${c.id}`}><UserCheck className="w-3 h-3 mr-1" />Reactivar</Button>)}
            {onBehalf && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => run(() => axios.post(`${API}/duty/check-in`, { crew_id: c.id }), 'Presentación registrada en su nombre')} data-testid={`crew-duty-in-${c.id}`}><LogIn className="w-3 h-3 mr-1" />Presentar</Button>}
            {onBehalf && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => run(() => axios.post(`${API}/duty/check-out`, { crew_id: c.id }), 'Fin de servicio registrado en su nombre')} data-testid={`crew-duty-out-${c.id}`}><LogOut className="w-3 h-3 mr-1" />Fin servicio</Button>}
            <TempPasswordDialog result={result} onClose={() => setResult(null)} />
        </div>
    );
}
